import { useEffect, useState } from "react";
import uuid from "react-uuid";

import { fetchOrder } from "../api/fetchOrder";
import type { ICustomerQuery, IOrdersEdgesNode } from "../interface/customer";
import type { ILineItem, IOrderInfo, IReturnLineItem } from "../interface/order";
import { OrderFinancialStatus } from "../types/storefront.types";
import { extractId } from "../utils/common";
import { sortOrderItems } from "../utils/order";

const STORE_NAME = import.meta.env.VITE_REACT_APP_NAME as string;

export default function useOrderDetail({
  lang,
  order,
  customer,
  handle,
  orderNotFound,
}: {
  lang: "ko" | "en" | "jp";
  order: IOrdersEdgesNode | null | undefined;
  customer: ICustomerQuery["customer"];
  handle?: string;
  orderNotFound?: boolean;
}) {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /* 주문 진행 내역을 관리하는 state - (unfulfilled, fulfilled, onhold) */
  const [completeOrder, setCompleteOrder] = useState<ILineItem[]>([]);
  /* 반품 내역을 관리하는 state - (returnRequested, returnApproved) */
  const [returnOrder, setReturnOrder] = useState<IReturnLineItem[]>([]);
  /* 환불 내역을 관리하는 state - (canceled, refunded) */
  const [refundOrder, setRefundOrder] = useState<ILineItem[]>([]);
  /* 반품 배송비를 관리하는 state */
  const [totalReturnShippingFee, setTotalReturnShippingFee] = useState<number>(0);
  /* 주문 정보를 관리하는 state */
  const [orderInfo, setOrderInfo] = useState<IOrderInfo | null>(null);

  useEffect(() => {
    setIsLoading(true);

    if (!order) {
      if (handle && orderNotFound) void getOrder();
      return;
    }

    setCompleteOrder([]);
    setReturnOrder([]);
    setRefundOrder([]);
    setTotalReturnShippingFee(0);

    // 반품 신청 여부를 빠르게 확인하기 위해 customAttributes를 사용
    // 기존 메타필드(returnStatus)가 업데이트 되기 전에 Storefront API에서 바로 상태 확인 가능
    // TODO: 현재 웰빙만 작업되어 있음, 추후 모든 스토어 변경하도록 수정 필요
    const returnStatus = order.customAttributes.find((item) => item.key === "returnStatus")?.value || order.returnStatus?.value;

    const isReturnOrPartiallyRefunded =
      order.financialStatus !== OrderFinancialStatus.Refunded &&
      (returnStatus === "requested" ||
        returnStatus === "approved" ||
        order.financialStatus === OrderFinancialStatus.PartiallyRefunded ||
        order.financialStatus === OrderFinancialStatus.PartiallyPaid);

    // 아카이브된 상품이거나 삭제된 상품의 경우 어드민 API를 호출
    const hasDeletedProduct = order.lineItems.edges.some((lineItem) => lineItem.node.variant === null);

    if (isReturnOrPartiallyRefunded || hasDeletedProduct) {
      void getOrder();
    } else {
      const orderLineItems = order.lineItems.edges.map(({ node }) => {
        return {
          ...node,
          id: uuid(),
          image: node.variant?.image,
          variantTitle: node.variant?.title, // variant가 null인 경우 size 표기를 위해 variantTitle를 가져옴
          variant: node.variant,
          originalTotalPrice: {
            ...node.originalTotalPrice,
            amount: (parseFloat(node.originalTotalPrice.amount) / node.quantity).toString(),
          },
          discountedTotalPrice: {
            ...node.discountedTotalPrice,
            amount: parseFloat(node.discountedTotalPrice.amount).toString(),
          },
          originalQuantity: node.quantity,
        };
      });

      if (order.financialStatus === OrderFinancialStatus.Refunded) {
        // 취소 or 전체 환불을 어드민에서 처리해줄 경우
        setRefundOrder(orderLineItems as ILineItem[]);
      } else {
        setCompleteOrder(orderLineItems as ILineItem[]);
      }

      setIsLoading(false);
    }
  }, [handle, order?.id, order?.financialStatus, order?.returnStatus?.value, orderNotFound]);

  const getOrder = async () => {
    try {
      const response = await fetchOrder({
        storeName: STORE_NAME,
        domain: lang === "ko" ? "" : "intl",
        orderId: order?.id ? extractId(order.id) : (handle ?? ""),
        customerId: customer.id,
      });

      if (!response) {
        window.location.href = "/account/order-history";
        return;
      }

      setTotalReturnShippingFee(response.totalReturnShippingFee);
      setCompleteOrder(response.lineItems);
      setReturnOrder(response.returnLineItems);
      setRefundOrder(response.refundLineItems);
      setOrderInfo(response.orderInfo);
    } catch (error) {
      window.location.href = "/account/order-history";
    } finally {
      setIsLoading(false);
    }
  };

  return {
    completeOrder: sortOrderItems([...completeOrder]),
    returnOrder: sortOrderItems([...returnOrder]),
    refundOrder: sortOrderItems([...refundOrder]),
    totalReturnShippingFee,
    orderInfo,
    isLoading,
  };
}
